/**
 * Product Tabs — World-Class B2B Experience
 * Features: tab switching, sticky-header specs table, column sorting,
 * debounced search with highlight, lazy video embeds, model selection guard.
 */

function normalizeCommonSpecsText(value) {
  return String(value || '')
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/&(nbsp|#160);/gi, ' ')
    .replace(/[\u00A0\u200B-\u200D\uFEFF]/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/[|\t\r\n]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function hasMeaningfulCommonSpecsMarkup(value) {
  return normalizeCommonSpecsText(value).length > 0;
}

function isModelLikeValue(value) {
  return /[A-Z]{1,}[A-Z0-9-]{1,}|\d{3,}|[A-Z]+\d+[A-Z0-9-]*/i.test(String(value || '').trim());
}

function isModelLikeCommonSpecsNode(root) {
  if (!root) return false;

  const table = root.querySelector('table');
  if (!table) return false;

  const headerCells = Array.from(table.querySelectorAll('thead th'));
  const firstHeader = normalizeCommonSpecsText(headerCells[0]?.textContent || '');
  const looksLikeModelHeader =
    firstHeader.includes('型號') ||
    firstHeader.includes('型番') ||
    firstHeader.includes('model') ||
    firstHeader.includes('sku');

  const bodyRows = Array.from(table.querySelectorAll('tbody tr')).filter((row) =>
    Array.from(row.querySelectorAll('th, td')).some((cell) => normalizeCommonSpecsText(cell.textContent).length > 0)
  );
  const firstColumnValues = bodyRows
    .map((row) => {
      const firstCell = row.querySelector('th, td');
      return normalizeCommonSpecsText(firstCell?.textContent || '');
    })
    .filter(Boolean);

  const modelLikeCount = firstColumnValues.filter(isModelLikeValue).length;
  const firstRowCellCount = Array.from(bodyRows[0]?.querySelectorAll?.('th, td') || []).filter((cell) =>
    normalizeCommonSpecsText(cell.textContent).length > 0
  ).length;
  const smallModelTableDup =
    looksLikeModelHeader &&
    bodyRows.length >= 2 &&
    firstRowCellCount >= 2 &&
    firstColumnValues.length === modelLikeCount;
  const enoughModelRows = bodyRows.length >= 20;
  const mostlyModelValues = firstColumnValues.length >= 5 && modelLikeCount / firstColumnValues.length >= 0.6;

  return looksLikeModelHeader && (smallModelTableDup || enoughModelRows || mostlyModelValues);
}

function getTableRows(table) {
  if (!table) return [];
  const bodyRows = Array.from(table.querySelectorAll('tbody tr'));
  if (bodyRows.length) return bodyRows;
  return Array.from(table.querySelectorAll('tr')).filter((row) => !row.closest('thead'));
}

function getRowCells(row) {
  return Array.from(row.querySelectorAll('th, td')).map((cell) => normalizeCommonSpecsText(cell.textContent));
}

function getTableHeaders(table) {
  if (!table) return [];
  const theadHeaders = Array.from(table.querySelectorAll('thead th')).map((cell) => normalizeCommonSpecsText(cell.textContent));
  if (theadHeaders.length) return theadHeaders;
  return getRowCells(getTableRows(table)[0] || { querySelectorAll: () => [] });
}

function tableHasSingleMeaningfulColumn(table) {
  const headers = getTableHeaders(table).filter(Boolean);
  if (headers.length > 1) return false;

  const rows = getTableRows(table);
  if (!rows.length) return false;

  return rows.every((row) => getRowCells(row).filter(Boolean).length <= 1);
}

function findBestCommonSpecsKey(modelCode, keys) {
  const code = normalizeCommonSpecsText(modelCode);
  if (!code) return null;
  if (keys.includes(code)) return code;

  const sortedKeys = [...keys].sort((a, b) => b.length - a.length);
  for (const key of sortedKeys) {
    if (code.startsWith(key)) return key;
  }
  return null;
}

function replaceSpecsTableWithMergedCommonSpecs(specsTable, commonTable) {
  if (!specsTable || !commonTable) return false;
  if (!tableHasSingleMeaningfulColumn(specsTable)) return false;

  const commonHeaders = getTableHeaders(commonTable).filter(Boolean);
  if (commonHeaders.length < 2) return false;

  const commonRows = getTableRows(commonTable)
    .map((row) => getRowCells(row))
    .filter((cells) => cells.some(Boolean));
  if (!commonRows.length) return false;

  const commonMap = new Map();
  for (const cells of commonRows) {
    const key = cells[0];
    if (!key) continue;
    commonMap.set(key, cells);
  }

  const commonKeys = Array.from(commonMap.keys());
  const specsRows = getTableRows(specsTable)
    .map((row) => getRowCells(row))
    .filter((cells) => cells.some(Boolean));
  if (!commonKeys.length || !specsRows.length) return false;

  const mergedHeaders = ['型號', ...commonHeaders.slice(1)];
  const mergedRows = specsRows.map((cells) => {
    const modelCode = cells[0] || '';
    const matchedKey = findBestCommonSpecsKey(modelCode, commonKeys);
    const commonCells = matchedKey ? commonMap.get(matchedKey) : null;
    return [modelCode, ...(commonCells ? commonCells.slice(1) : new Array(commonHeaders.length - 1).fill(''))];
  });

  const newTable = document.createElement('table');
  const thead = document.createElement('thead');
  const headRow = document.createElement('tr');
  mergedHeaders.forEach((header) => {
    const th = document.createElement('th');
    th.textContent = header;
    headRow.appendChild(th);
  });
  thead.appendChild(headRow);
  newTable.appendChild(thead);

  const tbody = document.createElement('tbody');
  mergedRows.forEach((rowCells) => {
    const tr = document.createElement('tr');
    rowCells.forEach((value, idx) => {
      const cell = document.createElement(idx === 0 ? 'th' : 'td');
      cell.textContent = value;
      tr.appendChild(cell);
    });
    tbody.appendChild(tr);
  });
  newTable.appendChild(tbody);
  specsTable.replaceWith(newTable);
  return true;
}

function hasMeaningfulCommonSpecsNode(root) {
  if (!root) return false;

  if (root.querySelector('img, svg, canvas, video, iframe, object, embed')) {
    return true;
  }

  const tables = root.querySelectorAll('table');
  for (const table of tables) {
    const cells = table.querySelectorAll('th, td');
    for (const cell of cells) {
      if (normalizeCommonSpecsText(cell.textContent).length > 0) {
        return true;
      }
    }
  }

  return normalizeCommonSpecsText(root.textContent).length > 0;
}

function stripPdfUrlHash(url) {
  const s = String(url || '').trim();
  const i = s.indexOf('#');
  return i >= 0 ? s.slice(0, i) : s;
}

/** 型錄一律新分頁開啟（全螢幕原生 PDF viewer），不使用燈箱。 */
function openCatalogPdfInNewTab(url) {
  const u = stripPdfUrlHash(String(url || '').trim());
  if (!u) return;
  try {
    window.open(u, '_blank', 'noopener,noreferrer');
  } catch (_) { /* noop */ }
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    normalizeCommonSpecsText,
    hasMeaningfulCommonSpecsMarkup,
    isModelLikeCommonSpecsNode,
    tableHasSingleMeaningfulColumn,
    findBestCommonSpecsKey,
    hasMeaningfulCommonSpecsNode,
  };
}

if (typeof document !== 'undefined') {
document.addEventListener('DOMContentLoaded', function () {

  // ===== Utilities =====
  function debounce(fn, ms) {
    let t;
    return function () {
      clearTimeout(t);
      const a = arguments, c = this;
      t = setTimeout(function () { fn.apply(c, a); }, ms);
    };
  }

  function escapeHtml(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  function escapeRegex(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function cleanupEmptyCommonSpecsSection() {
    const outer = document.querySelector('.mdsty-common-specs-outer');
    if (!outer) return;

    const body = outer.querySelector('.mdsty-common-specs__body') || outer;
    const specsTabContent = document.getElementById('mdsty-tab-specs');
    const shouldHideModelTableDup = specsTabContent && isModelLikeCommonSpecsNode(body);

    if (!hasMeaningfulCommonSpecsNode(body) || shouldHideModelTableDup) {
      outer.remove();
    }
  }

  function syncCommonSpecsDesktopLayout() {
    const outer = document.querySelector('.product__container--two-columns .product--outer > .mdsty-common-specs-outer');
    const productMain = document.querySelector('.product__container--two-columns .product--outer > .product-main');
    if (!outer || !productMain) return;

    if (window.innerWidth >= 720) {
      outer.style.gridColumn = '1 / -1';
      outer.style.gridRow = '2';
      outer.style.justifySelf = 'start';
      outer.style.width = '60%';
      outer.style.maxWidth = '60%';
      outer.style.marginTop = '18px';
      productMain.style.gridRow = '1';
    } else {
      outer.style.removeProperty('grid-column');
      outer.style.removeProperty('grid-row');
      outer.style.removeProperty('justify-self');
      outer.style.removeProperty('width');
      outer.style.removeProperty('max-width');
      outer.style.removeProperty('margin-top');
      productMain.style.removeProperty('grid-row');
    }
  }

  function expandSpecsTableFromCommonSpecs() {
    const specsTable = document.querySelector('#mdsty-tab-specs table');
    const commonTable = document.querySelector('.mdsty-common-specs__body table');
    if (!specsTable || !commonTable) return false;
    return replaceSpecsTableWithMergedCommonSpecs(specsTable, commonTable);
  }

  // ===== Tab Switching =====
  const tabBtns = document.querySelectorAll('.mdsty-tab-btn');
  const tabContents = document.querySelectorAll('.mdsty-tab-content');
  if (tabBtns.length) {
    const hasActive = Array.from(tabBtns).some(b => b.classList.contains('active'));
    if (!hasActive) {
      tabBtns[0].classList.add('active');
      const fc = document.getElementById('mdsty-tab-' + tabBtns[0].dataset.tab);
      if (fc) fc.classList.add('active');
    }
    tabBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        tabBtns.forEach(b => b.classList.remove('active'));
        tabContents.forEach(c => c.classList.remove('active'));
        btn.classList.add('active');
        const tc = document.getElementById('mdsty-tab-' + btn.dataset.tab);
        if (tc) tc.classList.add('active');
      });
    });
  }

  // ===== Deduplicate Specs Tables =====
  function deduplicateSpecs() {
    const commonSpecs = document.querySelector('.mdsty-common-specs');
    const specsTabBtn = document.querySelector('.mdsty-tab-btn[data-tab="specs"]');
    const specsTabContent = document.getElementById('mdsty-tab-specs');

    if (!commonSpecs || !specsTabBtn || !specsTabContent) return;

    // Check Specs Tab Content
    const table = specsTabContent.querySelector('table');
    if (!table) return; // No table in specs tab, keep it (might be other content)

    const rows = table.querySelectorAll('tr');
    if (rows.length === 0) return;

    // Heuristics: "Series List" vs "Model List"
    // Series List: Few rows (< 20) AND First column often contains "型" (Type).
    // Model List: Many rows (> 20) OR First column contains alphanumeric SKUs (e.g. SVS1020).

    let isSeriesList = false;
    let typeCount = 0;
    let skuCount = 0;

    // Check first 10 data rows
    const maxCheck = Math.min(rows.length, 15);
    let checked = 0;

    for (let i = 0; i < maxCheck; i++) {
      const row = rows[i];
      if (row.parentNode.tagName === 'THEAD') continue; // Skip header

      const cell = row.querySelector('th, td');
      if (!cell) continue;

      const txt = cell.textContent.trim();
      if (!txt) continue;

      checked++;
      // Check for "Type" indicators (Chinese or English context)
      if (txt.includes('型') || txt.includes('Series') || txt.includes('Type')) {
        typeCount++;
      } else if (txt.match(/[A-Z0-9-]{3,}/)) {
        // Likely SKU
        skuCount++;
      }
    }

    if (checked > 0) {
      // Decision Logic
      // If mostly "Type" strings OR very few rows -> Series List
      if (typeCount > skuCount || (rows.length < 10 && skuCount < 3)) {
        isSeriesList = true;
      }
    }

    if (isSeriesList) {
      console.log('Antigravity: Detected Series List in Specs Tab. (Auto-hide DISABLED by user request)');
      // specsTabBtn.style.display = 'none';
      // specsTabContent.style.display = 'none';

      // Hide Model Selector (Picker) - DISABLED RESTORATION
      // const picker = document.querySelector('[data-mdsty-model-picker]');
      // if (picker) picker.style.display = 'none';

      // Disable ATC Guard (allow pure text quote), but KEEP PICKER VISIBLE
      document.body.classList.add('mdsty-no-model-selection');

      // If Specs tab was active, switch to Intro or next available
      /*
      if (specsTabBtn.classList.contains('active')) {
        specsTabBtn.classList.remove('active');
        specsTabContent.classList.remove('active');

        const nextBtn = document.querySelector('.mdsty-tab-btn:not([style*="none"])');
        if (nextBtn && nextBtn.dataset.tab !== 'specs') {
          nextBtn.click();
        }
      }
      */
      return; // Done
    }

    /*
    console.log('Antigravity: Detected Model List in Specs Tab. Hiding Common Specs block.');
    if (commonSpecs.parentNode.classList.contains('mdsty-common-specs-outer')) {
      commonSpecs.parentNode.style.display = 'none';
    } else {
      commonSpecs.style.display = 'none';
    }
    */
  }

  // Run deduplication
  deduplicateSpecs();

  // ===== TSV → Table builder =====
  function isProbablyHeaderRow(cols) {
    const j = cols.join(' ').toLowerCase();
    return j.includes('型號') || j.includes('規格') || j.includes('特性') || j.includes('code');
  }
  function splitCols(line) {
    let cols = String(line || '').split('\t');
    if (cols.length === 1) cols = String(line || '').split('|');
    return cols.map(c => c.trim()).filter(c => c.length > 0);
  }
  /** 每頁列數固定（UI 已移除，使用者不可調） */
  var SPEC_PAGE_SIZE = 80;
  var _specCurrentPage = 1;
  var _specActiveTerm = '';
  var _specAllRows = [];
  var _specFilteredRows = [];

  function _makeRow(cols) {
    var tr = document.createElement('tr');
    cols.forEach(function (c, idx) {
      var cell = document.createElement(idx === 0 ? 'th' : 'td');
      cell.innerHTML = escapeHtml(c);
      tr.appendChild(cell);
    });
    return tr;
  }

  function getSpecsTable() {
    return document.querySelector('#mdsty-tab-specs table');
  }

  function getSpecsRows(table) {
    if (!table) return [];
    return Array.from(table.querySelectorAll('tr')).filter(function (row, idx) {
      if (row.closest('thead')) return false;
      if (!row.querySelector('th, td')) return false;
      if (idx === 0 && row.querySelectorAll('th').length > 0 && row.querySelectorAll('td').length === 0) return false;
      return true;
    });
  }

  function getFilteredSpecsRows(table, term) {
    return getSpecsRows(table).filter(function (row) {
      if (!term) return true;
      return (row.innerText || '').toLowerCase().includes(term);
    });
  }

  function getSpecTotalPages() {
    var totalRows = _specFilteredRows.length;
    var pageSize = SPEC_PAGE_SIZE;
    return totalRows > 0 ? Math.ceil(totalRows / pageSize) : 1;
  }

  function renderSpecsPagination(table, filteredRows) {
    if (!table) return;

    _specAllRows = getSpecsRows(table);
    _specFilteredRows = Array.isArray(filteredRows) ? filteredRows : getFilteredSpecsRows(table, _specActiveTerm);

    var pageSize = SPEC_PAGE_SIZE;

    var totalRows = _specAllRows.length;
    var filteredTotal = _specFilteredRows.length;
    var totalPages = filteredTotal > 0 ? Math.ceil(filteredTotal / pageSize) : 1;
    if (_specCurrentPage > totalPages) _specCurrentPage = totalPages;
    if (_specCurrentPage < 1) _specCurrentPage = 1;

    var startIndex = filteredTotal === 0 ? 0 : (_specCurrentPage - 1) * pageSize;
    var endIndex = Math.min(startIndex + pageSize, filteredTotal);
    var visibleRows = _specFilteredRows.slice(startIndex, endIndex);

    _specAllRows.forEach(function (row) {
      row.style.display = 'none';
    });
    visibleRows.forEach(function (row) {
      row.style.display = '';
    });

    var filterMeta = document.getElementById('spec-filter-meta');
    if (filterMeta) {
      filterMeta.textContent = _specActiveTerm
        ? ('搜尋結果 ' + filteredTotal + '/' + totalRows + ' 列')
        : (totalRows + ' 列');
    }

    var pageInfo = document.getElementById('spec-page-info');
    if (pageInfo) {
      pageInfo.textContent = filteredTotal === 0
        ? '第 0/0 頁'
        : ('第 ' + _specCurrentPage + '/' + totalPages + ' 頁');
    }

    var prevBtn = document.getElementById('spec-page-prev');
    var nextBtn = document.getElementById('spec-page-next');
    if (prevBtn) prevBtn.disabled = filteredTotal === 0 || _specCurrentPage <= 1;
    if (nextBtn) nextBtn.disabled = filteredTotal === 0 || _specCurrentPage >= totalPages;

    var jumpInput = document.getElementById('spec-page-jump');
    var jumpBtn = document.getElementById('spec-page-jump-apply');
    if (jumpInput) {
      jumpInput.disabled = filteredTotal === 0;
      jumpInput.placeholder = filteredTotal === 0 ? '0' : ('1-' + totalPages);
      jumpInput.max = String(totalPages);
    }
    if (jumpBtn) jumpBtn.disabled = filteredTotal === 0;
  }

  function buildTableFromTsv(tsv) {
    var lines = String(tsv || '').split(/\r?\n/).map(function (l) { return l.trim(); }).filter(Boolean);
    if (!lines.length) return null;
    var first = splitCols(lines[0]);
    var hasHeader = isProbablyHeaderRow(first);
    var table = document.createElement('table');
    if (hasHeader) {
      var thead = document.createElement('thead');
      var tr = document.createElement('tr');
      first.forEach(function (c) { var th = document.createElement('th'); th.innerHTML = escapeHtml(c); tr.appendChild(th); });
      thead.appendChild(tr);
      table.appendChild(thead);
    }
    // Parse all data rows
    var allRows = [];
    for (var i = hasHeader ? 1 : 0; i < lines.length; i++) {
      var cols = splitCols(lines[i]);
      if (cols.length) allRows.push(cols);
    }
    var tbody = document.createElement('tbody');
    allRows.forEach(function (cols) { tbody.appendChild(_makeRow(cols)); });
    table.appendChild(tbody);
    return table;
  }

  // Inject TSV table
  var tsvScript = document.querySelector('script[data-mdsty-specs-tsv]');
  var tsvTarget = document.querySelector('[data-mdsty-specs-tsv-target]');
  if (tsvScript && tsvTarget && !tsvTarget.querySelector('table')) {
    try {
      var table = buildTableFromTsv(JSON.parse(tsvScript.textContent || '""'));
      if (table) {
        tsvTarget.appendChild(table);
      }
    } catch (_) { /* noop */ }
  }

  expandSpecsTableFromCommonSpecs();

  // ===== Column Sorting =====
  function initTableSorting(table, onSorted) {
    if (!table) return;
    const thead = table.querySelector('thead');
    if (!thead) return;
    const ths = thead.querySelectorAll('th');
    ths.forEach((th, colIdx) => {
      th.addEventListener('click', () => {
        const tbody = table.querySelector('tbody');
        if (!tbody) return;
        const rows = Array.from(tbody.querySelectorAll('tr'));
        const curDir = th.getAttribute('data-sort-dir');
        const newDir = curDir === 'asc' ? 'desc' : 'asc';
        // Clear all
        ths.forEach(h => h.removeAttribute('data-sort-dir'));
        th.setAttribute('data-sort-dir', newDir);
        rows.sort((a, b) => {
          const aVal = (a.children[colIdx]?.textContent || '').trim();
          const bVal = (b.children[colIdx]?.textContent || '').trim();
          const aNum = parseFloat(aVal);
          const bNum = parseFloat(bVal);
          if (!isNaN(aNum) && !isNaN(bNum)) return newDir === 'asc' ? aNum - bNum : bNum - aNum;
          return newDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        });
        rows.forEach(r => tbody.appendChild(r));
        if (typeof onSorted === 'function') onSorted();
      });
    });
  }

  // ===== Search Filter with Highlight =====
  const filterInput = document.getElementById('spec-filter');
  const filterClear = document.getElementById('spec-filter-clear');
  const pageJumpInput = document.getElementById('spec-page-jump');
  const pageJumpApply = document.getElementById('spec-page-jump-apply');
  const pagePrev = document.getElementById('spec-page-prev');
  const pageNext = document.getElementById('spec-page-next');

  function clearHighlights(root) {
    if (!root) return;
    root.querySelectorAll('.mdsty-highlight').forEach(el => {
      const parent = el.parentNode;
      parent.replaceChild(document.createTextNode(el.textContent), el);
      parent.normalize();
    });
  }

  function highlightText(node, regex) {
    if (node.nodeType === 3) { // text node
      const match = node.textContent.match(regex);
      if (match) {
        const span = document.createElement('span');
        span.className = 'mdsty-highlight';
        const idx = match.index;
        const after = node.splitText(idx);
        after.splitText(match[0].length);
        span.appendChild(after.cloneNode(true));
        after.parentNode.replaceChild(span, after);
        return 1;
      }
      return 0;
    }
    if (node.nodeType === 1 && node.childNodes && !/(script|style)/i.test(node.tagName) && node.className !== 'mdsty-highlight') {
      for (let i = 0; i < node.childNodes.length; i++) {
        i += highlightText(node.childNodes[i], regex);
      }
    }
    return 0;
  }

  function applySpecsFilter(termRaw, resetPage) {
    var table = getSpecsTable();
    if (!table) return;

    _specActiveTerm = String(termRaw || '').trim().toLowerCase();
    if (resetPage !== false) _specCurrentPage = 1;

    clearHighlights(table);
    var filteredRows = getFilteredSpecsRows(table, _specActiveTerm);
    renderSpecsPagination(table, filteredRows);

    if (_specActiveTerm) {
      var regex = new RegExp('(' + escapeRegex(_specActiveTerm) + ')', 'gi');
      filteredRows.forEach(function (row) {
        if (row.style.display === 'none') return;
        highlightText(row, regex);
      });
    }
  }

  const specsTable = getSpecsTable();
  if (specsTable) {
    initTableSorting(specsTable, function () {
      applySpecsFilter(_specActiveTerm, false);
    });
    applySpecsFilter('', true);
  }

  if (filterInput) {
    var applyFilter = debounce(function (termRaw) {
      applySpecsFilter(termRaw, true);
    }, 150);

    filterInput.addEventListener('input', function (e) { applyFilter(e.target.value); });
    if (filterClear) {
      filterClear.addEventListener('click', function () {
        filterInput.value = '';
        applySpecsFilter('', true);
        filterInput.focus();
      });
    }
  }

  if (pagePrev) {
    pagePrev.addEventListener('click', function () {
      if (_specCurrentPage <= 1) return;
      _specCurrentPage -= 1;
      applySpecsFilter(filterInput ? filterInput.value : _specActiveTerm, false);
    });
  }

  if (pageNext) {
    pageNext.addEventListener('click', function () {
      _specCurrentPage += 1;
      applySpecsFilter(filterInput ? filterInput.value : _specActiveTerm, false);
    });
  }

  function jumpToSpecsPage(pageValue) {
    var totalPages = getSpecTotalPages();
    var nextPage = parseInt(pageValue, 10);
    if (!nextPage || isNaN(nextPage)) nextPage = 1;
    nextPage = Math.max(1, Math.min(totalPages, nextPage));
    _specCurrentPage = nextPage;
    applySpecsFilter(filterInput ? filterInput.value : _specActiveTerm, false);
    if (pageJumpInput) pageJumpInput.value = String(nextPage);
  }

  if (pageJumpApply) {
    pageJumpApply.addEventListener('click', function () {
      jumpToSpecsPage(pageJumpInput ? pageJumpInput.value : '');
    });
  }

  if (pageJumpInput) {
    pageJumpInput.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      jumpToSpecsPage(pageJumpInput.value);
    });
  }

  // ===== Model Selector / TSV population =====
  const modelSelect = document.querySelector('select[data-mdsty-model-select]');
  const modelHint = document.querySelector('[data-mdsty-model-hint]');
  const openCodeListBtn = document.querySelector('[data-mdsty-open-code-list]');

  function setHint(text) { if (modelHint) modelHint.textContent = text || ''; }
  function activateSpecsTab() {
    const btn = document.querySelector('.mdsty-tab-btn[data-tab="specs"]');
    if (btn) btn.click();
    const anchor = document.getElementById('mdsty-tab-specs');
    if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (filterInput) setTimeout(() => filterInput.focus(), 250);
  }

  // ===== Searchable Combobox Controller =====
  const comboWrap = document.querySelector('[data-mdsty-combo]');
  const comboInput = document.querySelector('[data-mdsty-combo-input]');
  const comboList = document.querySelector('[data-mdsty-combo-list]');
  const comboClear = document.querySelector('[data-mdsty-combo-clear]');
  let comboItems = [];
  let comboFocusIdx = -1;

  function comboPopulate(models) {
    if (!comboList || !comboInput) return;
    comboItems = models;
    comboInput.disabled = false;
    comboInput.placeholder = '搜尋型號… (' + models.length + ' 個)';
    comboRender(models);
  }

  function comboRender(filtered) {
    if (!comboList) return;
    comboList.innerHTML = '';
    comboFocusIdx = -1;
    if (!filtered.length) {
      comboList.innerHTML = '<div class="product-form__combo-empty">找不到符合的型號</div>';
      return;
    }
    filtered.forEach((it, i) => {
      const div = document.createElement('div');
      div.className = 'product-form__combo-opt';
      div.setAttribute('role', 'option');
      div.dataset.value = it.model;
      div.dataset.idx = i;
      if (it.summary) div.dataset.summary = it.summary;
      div.textContent = it.model;
      if (modelSelect && modelSelect.value === it.model) div.classList.add('is-selected');
      div.addEventListener('click', () => comboSelect(it));
      comboList.appendChild(div);
    });
    const count = document.createElement('div');
    count.className = 'product-form__combo-count';
    count.textContent = '顯示 ' + filtered.length + ' / ' + comboItems.length + ' 個型號';
    comboList.appendChild(count);
  }

  function comboSelect(it) {
    if (!modelSelect || !comboInput) return;
    comboInput.value = it.model;
    modelSelect.value = it.model;
    modelSelect.dispatchEvent(new Event('change', { bubbles: true }));
    comboClose();
    comboClear?.classList.add('is-shown');
    comboList?.querySelectorAll('.product-form__combo-opt').forEach(el => {
      el.classList.toggle('is-selected', el.dataset.value === it.model);
    });
  }

  function comboFilter() {
    const q = (comboInput?.value || '').trim().toLowerCase();
    if (!q) { comboRender(comboItems); return; }
    const filtered = comboItems.filter(it => it.model.toLowerCase().includes(q));
    comboRender(filtered);
  }

  function comboOpen() {
    comboList?.classList.add('is-open');
    comboFilter();
  }

  function comboClose() {
    comboList?.classList.remove('is-open');
    comboFocusIdx = -1;
  }

  function comboFocusAt(idx) {
    const opts = comboList?.querySelectorAll('.product-form__combo-opt') || [];
    if (!opts.length) return;
    opts.forEach(el => el.classList.remove('is-focused'));
    comboFocusIdx = Math.max(0, Math.min(idx, opts.length - 1));
    opts[comboFocusIdx]?.classList.add('is-focused');
    opts[comboFocusIdx]?.scrollIntoView({ block: 'nearest' });
  }

  if (comboInput) {
    comboInput.addEventListener('focus', comboOpen);
    comboInput.addEventListener('input', () => {
      comboFilter();
      comboList?.classList.add('is-open');
      comboClear?.classList.toggle('is-shown', !!comboInput.value);
    });
    comboInput.addEventListener('keydown', e => {
      const opts = comboList?.querySelectorAll('.product-form__combo-opt') || [];
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!comboList?.classList.contains('is-open')) comboOpen();
        comboFocusAt(comboFocusIdx + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        comboFocusAt(comboFocusIdx - 1);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (comboFocusIdx >= 0 && opts[comboFocusIdx]) {
          const val = opts[comboFocusIdx].dataset.value;
          const it = comboItems.find(m => m.model === val);
          if (it) comboSelect(it);
        }
      } else if (e.key === 'Escape') {
        comboClose();
      }
    });
  }

  if (comboClear) {
    comboClear.addEventListener('click', () => {
      if (comboInput) comboInput.value = '';
      if (modelSelect) { modelSelect.value = ''; modelSelect.dispatchEvent(new Event('change', { bubbles: true })); }
      comboClear.classList.remove('is-shown');
      comboInput?.focus();
      comboFilter();
    });
  }

  document.addEventListener('click', e => {
    if (comboWrap && !comboWrap.contains(e.target)) comboClose();
  });

  function populateModelSelect(tsv) {
    if (!modelSelect) return;
    const lines = String(tsv || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const first = splitCols(lines[0] || '');
    const startIdx = isProbablyHeaderRow(first) ? 1 : 0;
    const uniq = new Set();
    const models = [];
    for (let i = startIdx; i < lines.length; i++) {
      const cols = splitCols(lines[i]);
      const m = (cols[0] || '').trim();
      if (!m || uniq.has(m)) continue;
      uniq.add(m);
      models.push({ model: m, summary: cols.slice(1, 3).join(' / ') });
    }
    if (!models.length) return;
    const keep = Array.from(modelSelect.options).slice(0, 1);
    modelSelect.innerHTML = '';
    keep.forEach(o => modelSelect.appendChild(o));
    models.forEach(it => {
      const opt = document.createElement('option');
      opt.value = it.model;
      opt.textContent = it.model;
      if (it.summary) opt.dataset.mdstySummary = it.summary;
      modelSelect.appendChild(opt);
    });
    modelSelect.disabled = false;
    comboPopulate(models);
    if (models.length === 1) {
      modelSelect.value = models[0].model;
      modelSelect.dispatchEvent(new Event('change', { bubbles: true }));
      if (comboInput) { comboInput.value = models[0].model; comboClear?.classList.add('is-shown'); }
    } else {
      setHint('共 ' + models.length + ' 個型號可選');
    }
  }

  // Safety Bypass if no models found
  function checkGuardBypass() {
    if (!modelSelect || !modelSelect.options || modelSelect.options.length <= 1) {
      console.log('Antigravity: No models found. Bypassing selection guard.');
      document.body.classList.add('mdsty-no-model-selection');
      const picker = document.querySelector('[data-mdsty-model-picker]');
      if (picker) picker.style.display = 'none';
    }
  }

  function populateFromRenderedTable() {
    if (!modelSelect) return;
    const table = document.querySelector('#mdsty-tab-specs table');
    if (!table) return;
    const rows = table.querySelectorAll('tbody tr');
    const uniq = new Set();
    const models = [];
    const keep = Array.from(modelSelect.options).slice(0, 1);
    modelSelect.innerHTML = '';
    keep.forEach(o => modelSelect.appendChild(o));
    rows.forEach(tr => {
      const fc = tr.querySelector('th,td');
      const m = (fc?.textContent || '').trim();
      if (!m || uniq.has(m)) return;
      uniq.add(m);
      const opt = document.createElement('option');
      opt.value = m; opt.textContent = m;
      modelSelect.appendChild(opt);
      models.push({ model: m, summary: '' });
    });
    if (uniq.size) {
      modelSelect.disabled = false;
      comboPopulate(models);
      if (models.length === 1) {
        modelSelect.value = models[0].model;
        if (comboInput) {
          comboInput.value = models[0].model;
          comboClear?.classList.add('is-shown');
        }
      } else {
        setHint('共 ' + uniq.size + ' 個型號可選');
      }
    }
    checkGuardBypass();
  }

  function syncTableRowClick() {
    if (!modelSelect) return;
    const table = document.querySelector('#mdsty-tab-specs table');
    if (!table) return;
    const rows = (table.querySelector('tbody') || table).querySelectorAll('tr');
    rows.forEach(tr => {
      tr.addEventListener('click', () => {
        const fc = tr.querySelector('th,td');
        const m = (fc?.textContent || '').trim();
        if (!m) return;
        modelSelect.value = m;
        modelSelect.dispatchEvent(new Event('change', { bubbles: true }));
        if (comboInput) { comboInput.value = m; comboClear?.classList.add('is-shown'); }
      });
    });
  }

  if (openCodeListBtn) openCodeListBtn.addEventListener('click', activateSpecsTab);

  // ATC guard
  document.addEventListener('click', e => {
    // Escape hatch if model selection is disabled
    if (document.body.classList.contains('mdsty-no-model-selection')) return;

    const atcBtn = e.target?.closest?.('[data-product-atc]');
    if (atcBtn) {
      const form = atcBtn.closest?.('form');
      const sel = form?.querySelector?.('select[data-mdsty-model-select]');
      if (sel && !sel.value) {
        e.preventDefault(); e.stopImmediatePropagation?.(); e.stopPropagation();
        if (comboWrap) comboWrap.setAttribute('aria-invalid', 'true');
        if (comboInput) comboInput.focus(); else sel.focus();
        setHint('請先選擇型號後再送出詢價');
        activateSpecsTab();
      }
    }
  }, true);

  if (modelSelect) {
    if (tsvScript) {
      try { populateModelSelect(JSON.parse(tsvScript.textContent || '""')); } catch (_) { }
    } else {
      populateFromRenderedTable();
    }
    checkGuardBypass();
    modelSelect.addEventListener('change', () => {
      const v = modelSelect.value;
      const opt = modelSelect.selectedOptions?.[0];
      const summary = opt?.dataset?.mdstySummary;

      // Robust Input Finding
      let hiddenInput = null;
      if (modelSelect.form) hiddenInput = modelSelect.form.querySelector('.js-mdsty-model-input');
      if (!hiddenInput) hiddenInput = modelSelect.closest('form')?.querySelector('.js-mdsty-model-input');
      if (!hiddenInput) hiddenInput = document.querySelector('.js-mdsty-model-input'); // Fallback

      if (!hiddenInput) hiddenInput = document.querySelector('.js-mdsty-model-input'); // Fallback

      if (hiddenInput) {
        hiddenInput.value = v;
        hiddenInput.setAttribute('value', v); // Force attribute update for some browsers
      }

      if (!v) {
        setHint('請先選擇型號後再送出詢價');
        if (comboWrap) comboWrap.setAttribute('aria-invalid', 'true');
        return;
      }
      if (comboWrap) comboWrap.removeAttribute('aria-invalid');
      if (comboInput && comboInput.value !== v) { comboInput.value = v; comboClear?.classList.add('is-shown'); }
      setHint(summary ? '已選：' + v + '（' + summary + '）' : '已選：' + v);
    });
    const form = modelSelect.closest('form');
    if (form) {
      const atcBtns = Array.from(form.querySelectorAll('[data-product-atc]'));
      const refreshAtcState = () => {
        if (document.body.classList.contains('mdsty-no-model-selection')) {
          atcBtns.forEach(btn => {
            btn.disabled = false;
            btn.setAttribute('aria-disabled', 'false');
          });
          return;
        }
        const disabled = !modelSelect.value;
        atcBtns.forEach(btn => {
          btn.disabled = disabled;
          btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        });
      };
      const clearAtcProcessing = () => {
        atcBtns.forEach(btn => {
          btn.classList.remove('processing', 'is-loading', 'product-form__submit-button--pulsing');
        });
        refreshAtcState();
      };
      // 勿在點擊後用 timeout 清 loading：header 永遠有 [data-atc-banner-cart-button]，
      // 會在 cart 尚未完成前就解除 disabled + 轉圈，與右上角 toast 時序脫節。
      // Initial check & observer for dynamic class changes? 
      // Actually deduplicateSpecs runs ONCE at load. so refreshAtcState call below is fine if we run it AFTER helpfully.
      // But refreshAtcState is defined locally. 
      // We might need to re-run it in deduplicateSpecs... but deduplicateSpecs runs EARLIER?
      // DOMContentLoaded runs sequentially.
      // deduplicateSpecs is called at line ~156.
      // logic here is at line ~680. 
      // So deduplicateSpecs runs logic FIRST (adds class to body).
      // Then this code runs.
      refreshAtcState();
      modelSelect.addEventListener('change', refreshAtcState);
      if (modelSelect.value) {
        modelSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
      document.addEventListener('product-form:submit:success', function (e) {
        if (e.target !== form && e.target !== form.closest('product-form') && !e.target?.contains?.(form)) return;
        clearAtcProcessing();
      });
      document.addEventListener('product-form:submit:error', function (e) {
        if (e.target !== form && e.target !== form.closest('product-form') && !e.target?.contains?.(form)) return;
        clearAtcProcessing();
      });

      form.addEventListener('submit', e => {
        if (document.body.classList.contains('mdsty-no-model-selection')) return;
        if (!modelSelect.value) {
          e.preventDefault();
          setHint('請先選擇型號後再送出詢價');
          if (comboWrap) comboWrap.setAttribute('aria-invalid', 'true');
          if (comboInput) comboInput.focus(); else modelSelect.focus();
          activateSpecsTab();
          return;
        }
      });
    }
    setTimeout(syncTableRowClick, 50);
  }

  // ===== Common Specs TSV =====
  const commonScript = document.querySelector('script[data-mdsty-common-specs-tsv]');
  const commonTarget = document.querySelector('[data-mdsty-common-specs-tsv-target]');
  if (commonScript && commonTarget && !commonTarget.querySelector('table')) {
    try {
      const table = buildTableFromTsv(JSON.parse(commonScript.textContent || '""'));
      if (table) { table.className = 'mdsty-common-specs__table'; commonTarget.appendChild(table); }
    } catch (_) { }
  }

  cleanupEmptyCommonSpecsSection();
  syncCommonSpecsDesktopLayout();
  window.addEventListener('resize', debounce(syncCommonSpecsDesktopLayout, 120));

  // ===== CAD URLs Rendering =====
  const cadScript = document.querySelector('script[data-mdsty-cad-urls]');
  const cadTarget = document.querySelector('[data-mdsty-cad-target]');
  if (cadScript && cadTarget) {
    try {
      const cadLinks = JSON.parse(cadScript.textContent || '[]');
      if (cadLinks.length) {
        const grid = document.createElement('ul');
        grid.className = 'mdsty-file-grid';
        cadLinks.forEach(function (item) {
          const li = document.createElement('li');
          li.className = 'mdsty-file-card';
          const typeMap = {
            'dxf': { icon: '📐', badge: 'mdsty-file-badge--dxf', label: 'DXF' },
            'dwg': { icon: '📐', badge: 'mdsty-file-badge--dxf', label: 'DWG' },
            'step': { icon: '🔧', badge: 'mdsty-file-badge--cad', label: 'STEP' },
            'pdf': { icon: '📄', badge: 'mdsty-file-badge--pdf', label: 'PDF' },
            '3d_configurator': { icon: '🌐', badge: 'mdsty-file-badge--ext', label: '3D' },
            'direct_download': { icon: '⬇️', badge: 'mdsty-file-badge--cad', label: 'DL' },
            'external': { icon: '🔗', badge: 'mdsty-file-badge--ext', label: 'EXT' },
          };
          const t = typeMap[item.type] || typeMap['external'];
          const isExternal = !item.url.match(/\.(dxf|dwg|step|stp|pdf)$/i);
          li.innerHTML =
            '<div class="mdsty-file-icon">' + t.icon + '</div>' +
            '<div class="mdsty-file-info">' +
            '<div class="mdsty-file-name">' + escapeHtml(item.label || 'CAD File') + '</div>' +
            '<div class="mdsty-file-meta"><span class="mdsty-file-badge ' + t.badge + '">' + t.label + '</span></div>' +
            '</div>' +
            '<a href="' + escapeHtml(item.url) + '" target="_blank" rel="noopener" class="mdsty-file-dl"' + (isExternal ? '' : ' download') + '>' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>' +
            (isExternal ? '開啟' : '下載') +
            '</a>';
          grid.appendChild(li);
        });
        cadTarget.appendChild(grid);
      }
    } catch (_) { /* noop */ }
  }

  // ===== 檔案下載 tab (custom.drawing_downloads_json → Shopify Files CDN) =====
  const drawingScript = document.querySelector('script[data-mdsty-drawing-downloads]');
  const drawingTarget = document.querySelector('[data-mdsty-drawing-downloads-target]');
  if (drawingScript && drawingTarget) {
    try {
      let drawingFiles = JSON.parse(drawingScript.textContent || '[]');
      if (typeof drawingFiles === 'string') {
        try {
          drawingFiles = JSON.parse(drawingFiles);
        } catch (e2) {
          drawingFiles = [];
        }
      }
      if (!Array.isArray(drawingFiles)) drawingFiles = [];
      if (drawingFiles.length) {
        const grid = document.createElement('ul');
        grid.className = 'mdsty-file-grid';
        drawingFiles.forEach(function (item) {
          if (!item || !item.url) return;
          const li = document.createElement('li');
          li.className = 'mdsty-file-card';
          const typeMap = {
            dxf: { icon: '📐', badge: 'mdsty-file-badge--dxf', label: 'DXF' },
            dwg: { icon: '📐', badge: 'mdsty-file-badge--dxf', label: 'DWG' },
            step: { icon: '🔧', badge: 'mdsty-file-badge--cad', label: 'STEP' },
            zip: { icon: '📦', badge: 'mdsty-file-badge--cad', label: 'ZIP' },
            cad: { icon: '📐', badge: 'mdsty-file-badge--cad', label: 'CAD' },
          };
          const ty = String(item.type || 'file').toLowerCase();
          const t = typeMap[ty] || { icon: '📁', badge: 'mdsty-file-badge--ext', label: ty.toUpperCase() };
          const url = String(item.url || '').trim();
          const isShopifyCdn = /cdn\.shopify\.com/i.test(url);
          li.innerHTML =
            '<div class="mdsty-file-icon">' + t.icon + '</div>' +
            '<div class="mdsty-file-info">' +
            '<div class="mdsty-file-name">' + escapeHtml(item.label || '檔案下載') + '</div>' +
            '<div class="mdsty-file-meta"><span class="mdsty-file-badge ' + t.badge + '">' + t.label + '</span></div>' +
            '</div>' +
            '<a href="' + escapeHtml(url) + '" target="_blank" rel="noopener" class="mdsty-file-dl"' +
            (isShopifyCdn ? ' download' : '') +
            '>' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>' +
            (isShopifyCdn ? '下載' : '開啟') +
            '</a>';
          grid.appendChild(li);
        });
        drawingTarget.appendChild(grid);
      }
    } catch (_) {
      /* noop */
    }
  }

  // ===== File Downloads from Playwright Enrichment =====
  const fdScript = document.querySelector('script[data-mdsty-file-downloads]');
  const fdTarget = document.querySelector('[data-mdsty-file-downloads-target]');
  if (fdScript && fdTarget) {
    try {
      const files = JSON.parse(fdScript.textContent || '[]');
      if (files.length) {
        const grid = document.createElement('ul');
        grid.className = 'mdsty-file-grid';
        files.forEach(function (item) {
          const li = document.createElement('li');
          li.className = 'mdsty-file-card';
          const typeMap = {
            'cad': { icon: '📐', badge: 'mdsty-file-badge--cad', label: 'CAD' },
            'pdf': { icon: '📄', badge: 'mdsty-file-badge--pdf', label: 'PDF' },
            'dxf': { icon: '📐', badge: 'mdsty-file-badge--dxf', label: 'DXF' },
            'dwg': { icon: '📐', badge: 'mdsty-file-badge--dxf', label: 'DWG' },
            'step': { icon: '🔧', badge: 'mdsty-file-badge--cad', label: 'STEP' },
          };
          const t = typeMap[item.type] || { icon: '📁', badge: 'mdsty-file-badge--ext', label: (item.type || 'FILE').toUpperCase() };
          li.innerHTML =
            '<div class="mdsty-file-icon">' + t.icon + '</div>' +
            '<div class="mdsty-file-info">' +
            '<div class="mdsty-file-name">' + escapeHtml(item.label || 'File') + '</div>' +
            '<div class="mdsty-file-meta"><span class="mdsty-file-badge ' + t.badge + '">' + t.label + '</span></div>' +
            '</div>' +
            '<a href="' + escapeHtml(item.url) + '" target="_blank" rel="noopener" class="mdsty-file-dl">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>' +
            '開啟' +
            '</a>';
          grid.appendChild(li);
        });
        fdTarget.appendChild(grid);
      }
    } catch (_) { /* noop */ }
  }

  // ===== Catalog PDF from EDM JSON =====
  // ===== Catalog PDF from EDM JSON =====
  const catScripts = document.querySelectorAll('script[data-mdsty-catalog-json]');
  catScripts.forEach(catScript => {
    // Find the sibling target div
    const catTarget = catScript.nextElementSibling;
    if (catTarget && catTarget.hasAttribute('data-mdsty-catalog-target')) {
      try {
        let cat = JSON.parse(catScript.textContent || '{}');
        // Handle double-encoded JSON (string inside string)
        if (typeof cat === 'string') {
          try {
            cat = JSON.parse(cat);
          } catch (e2) {
            console.warn('Error parsing inner JSON:', e2);
          }
        }

        var items = [];
        if (Array.isArray(cat)) {
          items = cat.filter(function(c) { return c && (c.pdfUrl || c.url); });
        } else if (cat && (cat.pdfUrl || cat.url)) {
          items = [cat];
        }
        if (items.length > 0) {
          var tabCatalog = document.getElementById('mdsty-tab-catalog');
          var productTitleFromDom = (tabCatalog && tabCatalog.getAttribute('data-mdsty-product-title')) || '';
          var grid = document.createElement('ul');
          grid.className = 'mdsty-file-grid';
          items.forEach(function(item, idx) {
            var url = item.pdfUrl || item.url || '';
            var cardTitle = (productTitleFromDom || '').trim();
            if (!cardTitle) cardTitle = item.name || item.label || '型錄檢視';
            if (items.length > 1) cardTitle = cardTitle + ' · PDF ' + (idx + 1);
            var li = document.createElement('li');
            li.className = 'mdsty-file-card';
            li.innerHTML =
              '<div class="mdsty-file-icon">📄</div>' +
              '<div class="mdsty-file-info">' +
              '<div class="mdsty-file-name">' + escapeHtml(cardTitle) + '</div>' +
              '<div class="mdsty-file-meta"><span class="mdsty-file-badge mdsty-file-badge--pdf">PDF</span></div>' +
              '</div>' +
              '<div class="mdsty-file-actions">' +
              '<button type="button" class="mdsty-file-preview" data-mdsty-pdf-open data-pdf-url="' + escapeHtml(url) + '">新分頁開啟</button>' +
              '<a href="' + escapeHtml(url) + '" target="_blank" rel="noopener" class="mdsty-file-dl" download>' +
              '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>' +
              '下載' +
              '</a>' +
              '</div>';
            grid.appendChild(li);
          });
          catTarget.appendChild(grid);
        }
      } catch (e) {
        console.warn('Error parsing catalog JSON:', e);
      }
    }
  });

  document.addEventListener('click', function(event) {
    const openBtn = event.target.closest('[data-mdsty-pdf-open]');
    if (openBtn) {
      event.preventDefault();
      openCatalogPdfInNewTab(openBtn.getAttribute('data-pdf-url') || '');
      return;
    }
  });

  // ===== Lazy Video Embeds =====
  document.querySelectorAll('[data-mdsty-video-url]').forEach(card => {
    const url = card.getAttribute('data-mdsty-video-url') || '';
    const placeholder = card.querySelector('[data-mdsty-video-placeholder]');
    if (!placeholder) return;

    // Set YouTube thumbnail if available
    let videoId = '';
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
    if (ytMatch) {
      videoId = ytMatch[1];
      placeholder.style.backgroundImage = 'url(https://img.youtube.com/vi/' + videoId + '/hqdefault.jpg)';
      placeholder.style.backgroundSize = 'cover';
      placeholder.style.backgroundPosition = 'center';
    }

    card.addEventListener('click', function handler() {
      card.removeEventListener('click', handler);
      if (ytMatch) {
        card.innerHTML = '<iframe src="https://www.youtube.com/embed/' + videoId + '?autoplay=1&rel=0" allow="autoplay; encrypted-media" allowfullscreen loading="lazy"></iframe>';
      } else if (url.includes('vimeo.com')) {
        const vimeoId = url.match(/vimeo\.com\/(\d+)/)?.[1] || '';
        card.innerHTML = '<iframe src="https://player.vimeo.com/video/' + vimeoId + '?autoplay=1" allow="autoplay" allowfullscreen loading="lazy"></iframe>';
      } else {
        card.innerHTML = '<video controls autoplay style="width:100%;height:100%;"><source src="' + escapeHtml(url) + '" type="video/mp4">您的瀏覽器不支援影片播放。</video>';
      }
    });
  });

});
}
